/**
 * useSpeechDictation — browser-native voice-to-text for the FutureBot composer.
 *
 * Wraps the Web Speech API (SpeechRecognition). Chrome/Edge/Safari support it;
 * where it's unavailable (`supported: false`) the mic button should not render.
 *
 * Finalized speech segments are delivered through `onFinal(chunk)` so the
 * composer can commit them into its draft; `interimText` carries the live
 * not-yet-final phrase for display. The recognition engine may end on its own
 * (silence, network) — state always resets through `onend`.
 *
 * `stop()` ends listening but still delivers pending finals; `abort()` discards
 * them (use on submit so late results don't repopulate the cleared draft).
 */

import { useCallback, useEffect, useRef, useState } from 'react';

const SpeechRecognitionImpl =
  typeof window !== 'undefined'
    ? window.SpeechRecognition || window.webkitSpeechRecognition
    : null;

export function useSpeechDictation({ onFinal, onError } = {}) {
  const [isListening, setIsListening] = useState(false);
  const [interimText, setInterimText] = useState('');
  const recognitionRef = useRef(null);
  const onFinalRef = useRef(onFinal);
  onFinalRef.current = onFinal;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const stop = useCallback(() => {
    recognitionRef.current?.stop();
  }, []);

  const abort = useCallback(() => {
    recognitionRef.current?.abort();
  }, []);

  const start = useCallback(() => {
    if (!SpeechRecognitionImpl || recognitionRef.current) return;

    const recognition = new SpeechRecognitionImpl();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = navigator.language || 'en-US';

    recognition.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const transcript = result[0]?.transcript || '';
        if (result.isFinal) {
          if (transcript.trim()) onFinalRef.current?.(transcript.trim());
        } else {
          interim += transcript;
        }
      }
      setInterimText(interim);
    };

    recognition.onerror = (event) => {
      // 'no-speech' and 'aborted' are routine; onend follows and resets state.
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        onErrorRef.current?.(event.error);
      }
    };

    recognition.onend = () => {
      recognitionRef.current = null;
      setIsListening(false);
      setInterimText('');
    };

    recognitionRef.current = recognition;
    setIsListening(true);
    try {
      recognition.start();
    } catch {
      recognitionRef.current = null;
      setIsListening(false);
    }
  }, []);

  const toggle = useCallback(() => {
    if (recognitionRef.current) stop();
    else start();
  }, [start, stop]);

  // Never leave the mic open after unmount.
  useEffect(() => () => recognitionRef.current?.abort(), []);

  return {
    supported: !!SpeechRecognitionImpl,
    isListening,
    interimText,
    start,
    stop,
    abort,
    toggle,
  };
}

export default useSpeechDictation;
